import React, { useEffect } from "react";
import Header from "../components/Header";
import Hero from "../components/Hero";
import Footer from "../components/Footer";
import FAQ from "../components/FAQ";
import AstrologerCard from "../components/AstrologerCard";
import Avatar from "../components/Avatar";
import { useAstrologers } from "../context/AstrologersContext";
import { Info, ThumbsUp, Sparkles, Eye } from "lucide-react";

const Sidebar = ({ astrologers }) => {
  return (
    <aside className="space-y-5">
      {/* Editorial Reviews */}
      <div id="reviews" className="sidebar-block">
        <h3 className="text-lg font-extrabold text-[#16a3a8] mb-3">Editorial Reviews</h3>
        <div className="divide-y divide-slate-200">
          {astrologers.slice(0, 3).map((a) => (
            <div key={a.id} className="flex items-center gap-3 py-3">
              <Avatar
                astrologer={a}
                sizeClass="w-12 h-12"
                ringClass="ring-2 ring-slate-100"
              />
              <div className="flex-1">
                <div className="font-bold text-[#0b1e3f] text-sm">{a.name}</div>
                <a href="#astrologers" className="text-xs text-[#16a3a8] font-semibold hover:underline">Learn More</a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommended Reads */}
      <div id="articles" className="sidebar-block">
        <h3 className="text-lg font-extrabold text-[#16a3a8] mb-3">Recommended Reads</h3>
        <ul className="space-y-3">
          {[
            "Step into your power in 2025",
            "5 questions a love astrologer can answer",
            "Vedic vs KP vs Nadi — which one is right for you?",
          ].map((t) => (
            <li key={t}>
              <div className="font-semibold text-[#0b1e3f] text-sm leading-snug">{t}</div>
              <a href="#" className="text-xs text-[#16a3a8] font-semibold hover:underline">Read More</a>
            </li>
          ))}
        </ul>
      </div>

      {/* Why Compare With Us */}
      <div className="sidebar-block">
        <h3 className="text-lg font-extrabold text-[#16a3a8] mb-3">Why Compare With Us</h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 text-[#0b1e3f] font-bold text-sm">
              <Info className="w-4 h-4 text-[#16a3a8]" /> Be informed
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              We've spent 250+ hours researching India's top astrologers to bring
              you a trustworthy, ranked list.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[#0b1e3f] font-bold text-sm">
              <ThumbsUp className="w-4 h-4 text-[#16a3a8]" /> Choose confidently
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Compare expertise, experience and availability — then pick the
              astrologer who matches your needs.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[#0b1e3f] font-bold text-sm">
              <Sparkles className="w-4 h-4 text-[#16a3a8]" /> Get clarity
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Connect over WhatsApp or call and get the guidance you've been
              seeking — today.
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};

const LongCopy = () => (
  <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-slate-800">
    <h2 className="text-2xl font-extrabold text-[#16a3a8]">
      The Top Astrologer in India — How We Ranked the Best
    </h2>
    <p className="mt-3 text-[15px] leading-relaxed">
      Searching for the <strong>top astrologer in India</strong> or the <strong>best astrologer in India</strong>?
      You are in the right place. topastrologerinindia.com is an independent editorial platform that ranks
      the best Indian astrologers based on years of experience, depth of expertise across Vedic, KP and Nadi
      systems, verified consultation count and real client feedback. Our 2026 ranking lists the top 5
      online astrologers in India — from masters of classical Jyotish to specialists in Tarot, Numerology,
      Vastu and Lal Kitab.
    </p>

    <div className="section-rule my-8" />

    <h2 className="text-2xl font-extrabold text-[#16a3a8]">
      Best Astrologer in India for Vedic, KP & Nadi Astrology
    </h2>
    <p className="mt-3 text-[15px] leading-relaxed">
      India's three classical astrology systems — <strong>Vedic, KP and Nadi</strong> — each offer a unique lens
      on your birth chart. The best astrologer in India is one who can combine these systems and translate
      them into clear, practical guidance. Our top two ranks are masters of all three: <strong>Sachin P
      Sharma</strong> (Master of Vedic, KP & Nadi Astrology with 22+ years of experience and 50,000+
      consultations) and <strong>Priya Sharma</strong> (Vedic, KP & Nadi Astrology Expert with 15+ years and
      30,000+ consultations). They are followed by trusted specialists in Tarot, Vastu and Horoscope
      Matching.
    </p>

    <div className="section-rule my-8" />

    <h3 className="text-2xl font-extrabold text-[#16a3a8]">
      Why Choose the Best Online Astrologer in India?
    </h3>
    <p className="mt-3 text-[15px] leading-relaxed">
      An online consultation with a top Indian astrologer gives you access to genuine, time-tested wisdom
      without geographical limits. There is no need to search for an &quot;astrologer near me&quot; — distance does
      not influence the accuracy of a chart. The best astrologers in India offer same-day WhatsApp readings,
      pan-India language support and clear, practical remedies.
    </p>
    <ul className="mt-3 space-y-3 text-[15px] leading-relaxed">
      <li><strong className="text-[#0b1e3f]">Convenience &amp; comfort:</strong> Consult from home over WhatsApp, phone or video — in Hindi, English, Sanskrit, Tamil, Marathi or Punjabi.</li>
      <li><strong className="text-[#0b1e3f]">Wide selection of experts:</strong> Compare Vedic, KP, Nadi, Tarot, Numerology and Vastu astrologers in one place.</li>
      <li><strong className="text-[#0b1e3f]">Anytime availability:</strong> Most listed astrologers offer same-day or next-day slots and quick WhatsApp replies.</li>
      <li><strong className="text-[#0b1e3f]">Verified &amp; trusted:</strong> Every astrologer is checked for experience, expertise, response time and client feedback.</li>
    </ul>

    <div className="section-rule my-8" />

    <h3 className="text-2xl font-extrabold text-[#16a3a8]">
      Who is the Best Astrologer in India in 2026?
    </h3>
    <p className="mt-3 text-[15px] leading-relaxed">
      As per our 2026 editorial ranking, <strong>Sachin P Sharma — the Messenger of Shiva</strong> — is the
      top astrologer in India for online Vedic, KP and Nadi consultations. With 22+ years of dedicated
      practice and a verified track-record of 50,000+ readings worldwide, he combines classical Jyotish with
      practical, Shaivite remedies. He is closely followed by <strong>Priya Sharma</strong>, a celebrated
      Vedic Acharya known for warmth, accuracy and her command of all three classical Indian systems.
    </p>

    <div className="section-rule my-8" />

    <h3 className="text-2xl font-extrabold text-[#16a3a8]">Are Online Astrologers in India Genuine?</h3>
    <p className="mt-3 text-[15px] leading-relaxed">
      Yes — when chosen carefully. Genuine astrologers are easy to spot: they ask for accurate birth
      details, explain what the chart shows, and offer remedies that make practical sense. Every astrologer
      on this list has been independently verified for experience, expertise and client reviews. Our top two
      ranks — <strong>Sachin P Sharma</strong> and <strong>Priya Sharma</strong> — are masters of Vedic, KP
      and Nadi systems.
    </p>
  </section>
);

const Home = () => {
  const { astrologers } = useAstrologers();

  useEffect(() => {
    document.title =
      "Top Astrologer in India 2026 | Best Astrologer in India — Vedic, KP & Nadi | topastrologerinindia.com";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <Hero />

      <main className="flex-1">
        {/* SEO H1 — visually hidden but crawlable */}
        <h1 className="sr-only">
          Top Astrologer in India 2026 — Best Astrologer in India for Vedic, KP & Nadi Astrology Online
        </h1>

        <section id="astrologers" className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-5">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#16a3a8] font-bold">
                    The 2026 Ranking
                  </div>
                  <h2 className="text-xl sm:text-3xl font-extrabold text-[#0b1e3f] mt-1 leading-tight">
                    Top 5 Astrologers in India — Best Astrologer in India 2026
                  </h2>
                </div>
                <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 shrink-0">
                  <Eye className="w-3.5 h-3.5" /> Independently ranked
                </div>
              </div>

              <div className="space-y-4">
                {astrologers.map((a, i) => (
                  <AstrologerCard key={a.id} astrologer={a} index={i} />
                ))}
              </div>

              {/* Confidentiality strip */}
              <div className="bg-slate-100 border border-slate-200 rounded px-4 py-3 flex items-center gap-2 text-sm text-slate-700">
                <Eye className="w-4 h-4 text-slate-500" />
                Click above for astrology consultations that are 100% private &amp; confidential.
              </div>
            </div>

            {/* Sidebar */}
            <Sidebar astrologers={astrologers} />
          </div>
        </section>

        <FAQ />
        <LongCopy />
      </main>

      <Footer />
    </div>
  );
};

export default Home;
